import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { isMobile } from 'react-device-detect';
import { TruthsortingStore } from './TruthsortingStore';
import { PuzzleNames, TruthsortingStatement } from '../../../utils/types';
import { determineValidities, onReveal, onSolve, saveTruthsorting, colorToShape, ShapeEnum } from './truthsortingUtils';
import PuzzleTopBar from '../shared/PuzzleTopBar';
import Instructions from './Instructions';
import MobileTruthsortingInstructions from './MobileInstructions';
import './Truthsorting.less';

import CircleIcon from 'url:./icons/circle.svg';
import SquareIcon from 'url:./icons/square.svg';
import TriangleIcon from 'url:./icons/triangle.svg';
import DiamondIcon from 'url:./icons/diamond.svg';
import HeartIcon from 'url:./icons/heart.svg';
import PentagonIcon from 'url:./icons/pentagon.svg';
import TrapezoidIcon from 'url:./icons/trapezoid.svg';
import StarIcon from 'url:./icons/star.svg';
import { useAmplitudeTracking, useFirstTimeInstructions, useIsMobileDimensions, usePageVisibilityPause, useStructuredDataEmbedding } from '../shared/utils';
import PauseCover from '../shared/PauseCover';
import { usePuzzleTracking } from '../../../utils/usePuzzleTracking';

const ShapeIcon: React.FC<{ shape: ShapeEnum }> = ({ shape }) => {
  switch (shape) {
    case ShapeEnum.Circle:
      return <img src={CircleIcon} className="shapeIcon" alt="circle" />;
    case ShapeEnum.Square:
      return <img src={SquareIcon} className="shapeIcon" alt="square" />;
    case ShapeEnum.Triangle:
      return <img src={TriangleIcon} className="shapeIcon" alt="triangle" />;
    case ShapeEnum.Diamond:
      return <img src={DiamondIcon} className="shapeIcon diamond" alt="diamond" />;
    case ShapeEnum.Heart:
      return <img src={HeartIcon} className="shapeIcon" alt="heart" />;
    case ShapeEnum.Pentagon:
      return <img src={PentagonIcon} className="shapeIcon" alt="pentagon" />;
    case ShapeEnum.Trapezoid:
      return <img src={TrapezoidIcon} className="shapeIcon" alt="trapezoid" />;
    case ShapeEnum.Star:
      return <img src={StarIcon} className="shapeIcon star" alt="star" />;
    default:
      return null;
  }
};

const Truthsorting = () => {
  const { slots, statements, revealed, solutionColors, solved } = TruthsortingStore.useState();

  // If revealed or solved, reorder statements to match the solution
  const displayedStatements = React.useMemo(() => {
    return revealed || solved
      ? solutionColors.map(color => statements.find(s => s.color === color)!)
      : statements;
  }, [revealed, solved, solutionColors, statements]);

  useFirstTimeInstructions(PuzzleNames.Truthsorting, TruthsortingStore as any);
  usePageVisibilityPause(TruthsortingStore as any, saveTruthsorting);
  useStructuredDataEmbedding(PuzzleNames.Truthsorting);
  useAmplitudeTracking(PuzzleNames.Truthsorting);
  usePuzzleTracking(PuzzleNames.Truthsorting);

  const useMobileStyles = isMobile || useIsMobileDimensions();

  // Colorblind mode state - load from cookie or default to false
  const [colorblindMode, setColorblindMode] = useState(() => {
    return Cookies.get('truthsortingColorblindMode') === 'true';
  });

  // The statement currently being dragged
  const [draggedStatement, setDraggedStatement] = useState<TruthsortingStatement | null>(null);
  // Vertical distance the dragged statement has moved from its starting position
  const [dragDistance, setDragDistance] = useState(0);
  // Index of the slot the mouse is currently hovering over during drag
  const [hoveredSlotIndex, setHoveredSlotIndex] = useState<number | null>(null);
  // Y position of mouse when drag started
  const initialMousePositionTop = useRef(0);
  // Array of slot DOM elements for position detection
  const slotElementsRef = useRef<(HTMLDivElement | null)[]>([]);
  // Ref version of hoveredSlotIndex for access in event handlers
  const hoveredSlotIndexRef = useRef<number | null>(null);
  // Index of the slot where the drag started
  const dragStartSlotIndexRef = useRef<number | null>(null);
  // Array of booleans indicating which statements are validly placed
  const [validities, setValidities] = useState<boolean[]>([]);

  // Calculate validities on initial render and whenever statements change
  useEffect(() => {
    setValidities(determineValidities(slots, displayedStatements));
  }, [slots, displayedStatements]);

  // Handle colorblind mode toggle
  const handleColorblindModeToggle = () => {
    const newValue = !colorblindMode;
    setColorblindMode(newValue);
    Cookies.set('truthsortingColorblindMode', String(newValue), { expires: 365 });
  };

  const onDragMove = (e: MouseEvent | TouchEvent) => {
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // Update drag distance by comparing mouse's current position (clientY) to its original position
    setDragDistance(clientY - initialMousePositionTop.current);

    // Detect which slot the mouse is currently over
    let foundIndex: number | null = null;
    slotElementsRef.current.forEach((slotEl, index) => {
      if (slotEl) {
        const rect = slotEl.getBoundingClientRect();
        if (clientY >= rect.top && clientY <= rect.bottom) {
          foundIndex = index;
        }
      }
    });

    // Update hovered slot if changed
    if (foundIndex !== hoveredSlotIndex) {
      setHoveredSlotIndex(foundIndex);
      hoveredSlotIndexRef.current = foundIndex;
    }
  };

  const onDragStart = (e: React.MouseEvent | React.TouchEvent, statement: TruthsortingStatement, statementIndex: number) => {
    // Don't allow dragging when revealed or solved
    if (revealed || solved) return;

    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    initialMousePositionTop.current = clientY;

    setDraggedStatement(statement);
    dragStartSlotIndexRef.current = statementIndex;

    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('touchmove', onDragMove);
    document.addEventListener('touchend', onDragEnd);
  };

  const onDragEnd = () => {
    const hoveredIndex = hoveredSlotIndexRef.current;
    const startIndex = dragStartSlotIndexRef.current;

    // If we're hovering over a different slot, swap the statements
    if (hoveredIndex !== null && startIndex !== null && hoveredIndex !== startIndex) {
      const newStatements = [...statements];
      // Swap the statements
      [newStatements[startIndex], newStatements[hoveredIndex]] =
        [newStatements[hoveredIndex], newStatements[startIndex]];

      TruthsortingStore.update(s => {
        s.statements = newStatements;
      });

      // Check if puzzle is solved
      const currentColors = newStatements.map(s => s.color);
      const isSolved = currentColors.every((color, index) => color === solutionColors[index]);

      if (isSolved) {
        onSolve();
      }
    }

    setDraggedStatement(null);
    setDragDistance(0);
    setHoveredSlotIndex(null);
    hoveredSlotIndexRef.current = null;
    dragStartSlotIndexRef.current = null;
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchmove', onDragMove);
    document.removeEventListener('touchend', onDragEnd);
  };

  return (
    <>
      <PuzzleTopBar
        puzzleName={PuzzleNames.Truthsorting}
        store={TruthsortingStore as any}
        onReveal={onReveal}
        onSave={saveTruthsorting}
        InstructionsComponent={useMobileStyles ? MobileTruthsortingInstructions : Instructions}
      />
      <div className={`puzzleContainer truthsortingContainer ${colorblindMode ? 'colorblindMode' : ''}`}>
        <PauseCover store={TruthsortingStore as any}/>
        <div className="positioningContainer">
          <div className="slotsOuterContainer">
            <div className="slotsInnerContainer">
              {slots.map((slot, i) => {
                const isHovered = hoveredSlotIndex === i && dragStartSlotIndexRef.current !== i;
                const isBlackSlot = !slot;
                const isWhiteSlot = slot;
                const isFirstSlot = i === 0;
                const isPrecededByWhiteSlot = i > 0 && slots[i - 1];
                const isPrecededByBlackSlot = i > 0 && !slots[i - 1];

                const blackSlotShouldHaveBorderTop = isBlackSlot && !isFirstSlot && !isPrecededByWhiteSlot;
                const whiteSlotShouldHaveBorderTop = isWhiteSlot && !isFirstSlot && !isPrecededByBlackSlot;
                const shouldHaveBorderTop = blackSlotShouldHaveBorderTop || whiteSlotShouldHaveBorderTop;

                return (
                  <div
                    key={i}
                    ref={(el) => (slotElementsRef.current[i] = el)}
                    className={`slot ${slot ? 'whiteSlot' : 'blackSlot'} ${isHovered ? 'hovered' : ''} ${shouldHaveBorderTop ? 'withBorderTop' : ''}`}
                  >
                    <div className="slotNumber">
                      {i + 1}
                    </div>
                    <div
                      className={`checkmarkContainer ${solved ? 'solving' : ''}`}
                      style={{
                        animationDelay: solved ? `${i * 0.15}s` : undefined,
                      }}
                    >
                      {validities[i] ? '✓' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="statementsContainer">
            {displayedStatements.map((statement, index) => (
              <div
                style={{
                  top: statement.color === draggedStatement?.color ? dragDistance : 0,
                  zIndex: statement.color === draggedStatement?.color ? 999 : 0,
                  animationDelay: solved ? `${index * 0.15}s` : undefined,
                }}
                onMouseDown={(e: React.MouseEvent) => onDragStart(e, statement, index)}
                onTouchStart={(e: React.TouchEvent) => onDragStart(e, statement, index)}
                className={`booleanStatement ${statement.color.toLowerCase()} ${colorblindMode ? 'withIcon' : ''} ${revealed || solved ? 'revealed' : ''} ${solved ? 'solving' : ''}`}
                key={statement.color}
              >
                {colorblindMode ? statement.colorblindText : statement.text}
                {colorblindMode && <ShapeIcon shape={colorToShape(statement.color)} />}
              </div>
            ))}
          </div>
        </div>
        {revealed &&
          <div className="outcomeBanner">
            Solution revealed. Try again tomorrow!
          </div>
        }
        {solved &&
          <div className="outcomeBanner">
            Solved! New puzzle tomorrow.
          </div>
        }
        <div className="colorblindModeToggle">
          <label>
            <input
              type="checkbox"
              checked={colorblindMode}
              onChange={handleColorblindModeToggle}
            />
            <span>Colorblind mode</span>
          </label>
        </div>
      </div>
    </>
  );
};

export default Truthsorting;